import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { clients } from '../../mocks/clients';

export default function AboutPage() {
  const values = [
    {
      icon: 'ri-medal-line',
      title: 'Uncompromising Quality',
      desc: 'Every project is executed to the highest standards, with meticulous attention to detail at every stage of construction.',
    },
    {
      icon: 'ri-time-line',
      title: 'On-Time Delivery',
      desc: 'We understand that time is money. Our rigorous project management ensures every deadline is met without exception.',
    },
    {
      icon: 'ri-handshake-line',
      title: 'Client Partnership',
      desc: 'We act as a true partner — a liaison between owner, architect, and contractor — ensuring seamless communication throughout.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Trusted Expertise',
      desc: '30 years of experience working with the world\'s most demanding luxury brands and government agencies.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative h-[55vh] w-full overflow-hidden">
        <img
          src="https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/030ca7c6dd5ae858c279654671f33954.jpeg"
          alt="About Reiken Construction Management"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 sm:px-8 md:px-20 w-full">
          <p className="text-white/60 text-xs uppercase tracking-[0.35em] mb-5 font-light">Est. 1995</p>
          <h1 className="text-white text-5xl sm:text-6xl md:text-8xl font-bold leading-tight">
            About<br />
            <span className="italic font-light">Reiken</span>
          </h1>
          <div className="w-12 h-px bg-white/40 mt-8" />
        </div>
      </section>

      <main className="pb-0">
        {/* Story */}
        <section className="py-16 sm:py-28 px-6 sm:px-8 md:px-20">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-5 gap-10 sm:gap-16 items-start">
              <div className="md:col-span-2">
                <p className="text-xs uppercase tracking-[0.3em] text-black/50 mb-5">Our Story</p>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight mb-6">
                  Three Decades of<br />
                  <span className="italic font-light">Excellence</span>
                </h2>
                <div className="w-16 h-0.5 bg-black mb-8" />
                <div className="relative overflow-hidden aspect-[3/4] hidden md:block">
                  <img
                    src="https://static.readdy.ai/image/25203592582f416a5bf3ad1425ed1a29/9116ad1db2939e1c7cff024b6fa06155.jpeg"
                    alt="Reiken project"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>

              <div className="md:col-span-3 space-y-6 sm:space-y-8 md:pt-16">
                <p className="text-lg sm:text-xl leading-relaxed text-black/80 font-light">
                  Established in 1995, Reiken Construction Mgmt., Inc. provides General Contracting and Construction Management services for the commercial and retail industries throughout New York City, Westchester County, Connecticut, Long Island and Northern New Jersey.
                </p>
                <p className="text-lg sm:text-xl leading-relaxed text-black/80 font-light">
                  Reiken has developed strong relationships with the leading department stores within the area; specializing in shop-in-shop installs, working for the individual vendor or directly for the store, including back of house and office renovations. During this time Reiken has maintained excellent client relations, in many cases renovating spaces seasonally, as well as being on the preferred contractor list of department stores such as Bloomingdales, Macy's, Saks Fifth Avenue, Lord &amp; Taylor and Bergdorf Goodman.
                </p>
                <p className="text-lg sm:text-xl leading-relaxed text-black/80 font-light">
                  Reiken has extensive experience in both new and retro-fit projects, including stand-alone stores, from the design and planning stage through client occupancy. Presided over by Michael Kennedy, Reiken acts as a liaison between owner and architect, managing sub-contractors and all other parties necessary to see the project through to fruition.
                </p>

                <div className="grid grid-cols-2 gap-6 sm:gap-8 pt-6 sm:pt-8 border-t border-black/10">
                  {[
                    { value: '1995', label: 'Founded' },
                    { value: 'NYC', label: 'Headquarters' },
                    { value: '5', label: 'States Served' },
                    { value: '500+', label: 'Projects Delivered' },
                  ].map((item) => (
                    <div key={item.label}>
                      <p className="text-2xl sm:text-3xl font-bold mb-1">{item.value}</p>
                      <p className="text-xs sm:text-sm uppercase tracking-widest text-black/50 font-light">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-16 sm:py-24 px-6 sm:px-8 md:px-20 bg-[#f8f7f5]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 sm:mb-16">
              <p className="text-xs uppercase tracking-[0.3em] text-black/50 mb-5">What Drives Us</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
                Our Core<br />
                <span className="italic font-light">Values</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-1">
              {values.map((value) => (
                <div
                  key={value.title}
                  className="bg-white p-8 sm:p-10 hover:bg-black hover:text-white transition-all duration-400 group cursor-default"
                >
                  <div className="w-10 h-10 flex items-center justify-center mb-6">
                    <i className={`${value.icon} text-3xl`}></i>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold mb-4">{value.title}</h3>
                  <p className="text-sm leading-relaxed opacity-60 font-light">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Clients */}
        <section className="py-16 sm:py-28 px-6 sm:px-8 md:px-20">
          <div className="max-w-7xl mx-auto">
            <div className="mb-12 sm:mb-16">
              <p className="text-xs uppercase tracking-[0.3em] text-black/50 mb-5">Who We Work With</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
                Our<br />
                <span className="italic font-light">Clients</span>
              </h2>
              <div className="w-16 h-0.5 bg-black mt-8" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 sm:gap-12 items-center">
              {clients.map((client) => (
                <div
                  key={client.name}
                  className="flex items-center justify-center grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300"
                >
                  <img
                    src={client.logo}
                    alt={client.name}
                    className="w-full h-auto max-w-[120px] sm:max-w-[160px] object-contain"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
